import React, { useEffect, useState, useRef, useCallback } from "react";
import axios from "axios";
import { toPng } from "html-to-image";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Form, Col, Row } from "react-bootstrap";
import Button from "../../../components/Button";
import {
  faCalendar,
  faCalendarAlt,
  faCloud,
  faCloudRain,
  faCloudSunRain,
  faDownload,
  faGlobe,
  faLocationDot,
  faMountain,
  faQuestionCircle,
  faRoad,
  faSnowflake,
  faSun,
  faTrophy,
} from "@fortawesome/free-solid-svg-icons";
import { useTranslation } from "react-i18next";
import DateFormat from "../../../components/DateFormat";
import LoadingContent from "../../../components/LoadingContent";
import Title from "../../../components/Admin/Title";
import { getAbbreviation } from "../../../utils/Standings";
import "../../../assets/style/imgnewsprevrally_1.css";
import GeminiRequest from "../../../components/GeminiRequest";

interface GPData {
  name: string;
  circuit: string;
  date: string;
  start_date: string;
  time: string;
  laps: string | null;
  flag_code: string;
  last_event_name: string;
  last_podium: string;
  most_wins_driver: string;
  most_poles_driver: string;
  most_wins: number;
  most_poles: number;
  last_pole_driver: string;
  last_pole_time: string;
  circuit_id: number;
  last_grand_prix_event_id: number;
  flag: string;
  round_number: number;
  championship: string;
  driver_standings: any;
  team_standings: any;
}

const PreGPRallyGenerator = () => {
  const { t } = useTranslation();
  const [data, setData] = useState<GPData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [readURL, setReadURL] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rallyLegs, setRallyLegs] = useState<any[]>([[], [], []]);
  const [geminiPromptText, setGeminiPromptText] = useState<string>("");
  const [generatedImageUrl, setGeneratedImageUrl] = useState<string | null>(
    null,
  );
  const componentRef = useRef<HTMLDivElement>(null);

  const [update, setUpdate] = useState(false);
  const [grandPrixEventId, setGrandPrixEventId] = useState(-1);
  const [nextGrandsPrix, setNextGrandsPrix] = useState<
    {
      name: string;
      id: number;
      type_race: string;
      type_races: number;
      type_championship: number;
    }[]
  >();

  // Estado para almacenar la imagen de fondo personalizada subida por el usuario
  const [customBgImage, setCustomBgImage] = useState<string | null>(null);

  // NUEVO: Estado para la URL externa
  const [targetUrl, setTargetUrl] = useState<string>("");

  // Manejar la carga del archivo de imagen personalizado
  const handleCustomImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const imageUrl = URL.createObjectURL(file);
      setCustomBgImage(imageUrl);
    }
  };

  // NUEVO: Función auxiliar para calcular las condiciones predominantes con separación limpia y nombres exactos
  const analyzeLegConditions = (stages: any[]) => {
    if (!stages || stages.length === 0) {
      return { surface: "-", weather: "-", roadType: "-" };
    }

    // --- 1. TIRE / ROAD TYPE ---
    const firstStageSurface = stages[0]?.tyre || "";
    const roadTypeRaw = firstStageSurface.trim().split(/\s+/)[0];
    const roadType = roadTypeRaw
      ? roadTypeRaw.charAt(0).toUpperCase() + roadTypeRaw.slice(1).toLowerCase()
      : "-";

    // --- 2. SUPERFICIE (Surface) ---
    const surfaces = stages.map((s) => s.surface.toLowerCase());
    const totalStages = stages.length;

    const isAllWet = surfaces.every((s) => s.includes("wet"));
    const isAllDry = surfaces.every((s) => s.includes("dry"));
    const wetCount = surfaces.filter((s) => s.includes("wet")).length;
    const dryCount = surfaces.filter((s) => s.includes("dry")).length;

    let surfaceResult = "Dry";
    if (isAllWet) {
      surfaceResult = "Wet";
    } else if (isAllDry) {
      surfaceResult = "Dry";
    } else if (wetCount > dryCount) {
      surfaceResult = "Mostly Wet";
    } else {
      surfaceResult = "Mostly Dry";
    }

    // --- 3. CLIMA (Weather) simplificado y limpio ---
    const rawWeathers = stages.map((s) => s.weather.trim());

    // Limpiar palabras de horario o basura inicial (ej: "Morning ", "Noon ", "Evening ")
    const weathers = rawWeathers.map((w) => {
      return w
        .replace(/^(morning|noon|evening|afternoon|night)\s+/i, "")
        .trim();
    });

    return {
      roadType,
      surface: surfaceResult,
      weather: getGeneralWeather(weathers),
    };
  };

  const getGeneralWeather = (weathers: string[]) => {
    if (!weathers || weathers.length === 0) return "Unknown";

    const totalElementos: number = weathers.length;

    // 1. Desglosar todas las palabras individuales
    const todasLasCondiciones: string[] = weathers.flatMap(
      (w: string): string[] => w.split(/\s+/),
    );

    // 2. Contar frecuencias de cada condición
    const frecuencias: Record<string, number> = {};
    todasLasCondiciones.forEach((cond: string) => {
      frecuencias[cond] = (frecuencias[cond] || 0) + 1;
    });

    // 3. Comprobar coincidencia total (100% de los elementos)
    for (const [cond, count] of Object.entries(frecuencias)) {
      if (count === totalElementos) {
        return formatTextClimate(cond);
      }
    }

    // 4. Comprobar mayoría (ej: 2 de 3 elementos)
    const umbralMayoria: number = Math.ceil(totalElementos / 2);
    for (const [cond, count] of Object.entries(frecuencias)) {
      if (count >= umbralMayoria) {
        // Aseguramos que la condición formateada aplique también PascalCase a cada palabra tras "Mostly"
        return `Mostly ${formatTextClimate(cond)}`;
      }
    }

    // 5. Fallback traducido al inglés con mayúsculas
    return "Varied Conditions";
  };

  const formatTextClimate = (texto: string): string => {
    const separadoPorEspacios = texto
      .replace(/([a-z])([A-Z])/g, "$1 $2")
      .toLowerCase();

    // Capitaliza la primera letra de cada palabra
    return separadoPorEspacios
      .split(" ")
      .map(
        (palabra: string) => palabra.charAt(0).toUpperCase() + palabra.slice(1),
      )
      .join(" ");
  };
  // NUEVO: Función modificada para leer la información de la URL y parsearla por consola
  const handleFetchUrlInfo = async () => {
    if (!targetUrl.trim()) return;

    try {
      const API_BASE =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5500"
          : "https://ligavirtualf1.es:5500";

      const response = await axios.get(
        `${API_BASE}/api/admin/fetchExternalUrl`,
        {
          params: { url: targetUrl },
        },
      );

      const htmlString = response.data?.data;

      if (typeof htmlString === "string") {
        const parser = new DOMParser();
        const doc = parser.parseFromString(htmlString, "text/html");

        const legsData: any[] = [];
        let currentLeg: any = null;

        const rows = doc.querySelectorAll("tr");

        rows.forEach((row) => {
          const textContent = row.textContent || "";

          if (
            textContent.includes("Download") ||
            textContent.includes("Championships") ||
            textContent.includes("Admins") ||
            textContent.includes("Discord") ||
            textContent.includes("Hirdetés") ||
            textContent.includes("Stage name")
          ) {
            return;
          }

          const isLegRow =
            row.querySelector(".lista_kiemelt") && textContent.includes("Leg");

          if (isLegRow) {
            const cols = row.querySelectorAll("td");
            const legTitleText = cols[0]?.textContent?.trim() || "";
            const legDistanceText = cols[1]?.textContent?.trim() || "";

            const cleanLegTitleMatch = legTitleText.match(/Leg\s+\d+/i);

            if (cleanLegTitleMatch) {
              const legName = cleanLegTitleMatch[0];
              let existingLeg = legsData.find((l) => l.leg === legName);

              if (!existingLeg) {
                currentLeg = {
                  leg: legName,
                  leg_distance: legDistanceText,
                  stages: [],
                };
                legsData.push(currentLeg);
              } else {
                currentLeg = existingLeg;
              }
            }
          } else {
            const cols = row.querySelectorAll("td");

            if (cols.length >= 5 && currentLeg) {
              const stageNumber = cols[0]?.textContent?.trim() || "";
              const stageName = cols[1]?.textContent?.trim() || "";
              const distance = cols[2]?.textContent?.trim() || "";
              const surface = cols[3]?.textContent?.trim() || "";
              const weather = cols[4]?.textContent?.trim() || "";
              const tyre = cols[6]?.textContent?.trim() || "";

              if (/^\d+$/.test(stageNumber)) {
                const stageExists = currentLeg.stages.some(
                  (s: any) =>
                    s.stage_number === stageNumber &&
                    s.stage_name === stageName,
                );

                if (!stageExists) {
                  currentLeg.stages.push({
                    stage_number: stageNumber,
                    stage_name: stageName,
                    distance: distance,
                    surface: surface,
                    weather: weather,
                    tyre: tyre,
                  });
                }
              }
            }
          }
        });

        // Procesar condiciones analizadas para cada leg
        const processedLegs = legsData.map((legItem) => {
          const conditions = analyzeLegConditions(legItem.stages);
          return {
            ...legItem,
            roadType: conditions.roadType,
            surface: conditions.surface,
            weather: conditions.weather,
          };
        });

        setRallyLegs(processedLegs);
        //console.log("processedLegs:", processedLegs);
        setReadURL(!readURL);
      }
    } catch (error) {
      console.error("Error al leer la URL:", error);
    }
  };

  // Cargar lista de grandes premios
  useEffect(() => {
    axios
      .get(
        `${
          process.env.NODE_ENV === "development"
            ? "http://localhost:5500"
            : "https://ligavirtualf1.es:5500"
        }/api/grandprix/getNextGrandsPrix`,
        {
          params: { type_races: 1 },
        },
      )
      .then(({ data }) => {
        setNextGrandsPrix(data.nextGrandsPrix);
      })
      .catch(({ response }) => {
        throw new Error(response);
      });
  }, [update]);

  // Cargar datos del GP seleccionado
  useEffect(() => {
    if (
      grandPrixEventId !== null &&
      grandPrixEventId !== undefined &&
      grandPrixEventId !== -1
    ) {
      setIsLoading(true);
      setData(null);
      setGeneratedImageUrl(null);
      setError(null);

      const API_BASE =
        process.env.NODE_ENV === "development"
          ? "http://localhost:5500"
          : "https://ligavirtualf1.es:5500";

      axios
        .get(`${API_BASE}/api/grandprix/getNewsGenerateInfo`, {
          params: { grand_prix_event_id: grandPrixEventId },
        })
        .then(({ data }) => {
          setData(data);
        })
        .catch((err) => {
          console.error("Error fetching GP info:", err);
          setError("No se pudo cargar la información del Gran Premio.");
          setIsLoading(false);
        });
    }
  }, [grandPrixEventId, readURL]);

  // Función para generar la imagen
  const handleGenerateImage = useCallback(() => {
    if (componentRef.current === null) return;

    toPng(componentRef.current, {
      quality: 1.0,
      canvasWidth: 1920,
      canvasHeight: 1080,
      style: {
        overflow: "hidden",
      },
    })
      .then((dataUrl: string) => {
        setGeneratedImageUrl(dataUrl);
      })
      .catch((err) => {
        console.error("Error generating image:", err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [componentRef]);

  // Efecto automático: en cuanto llegan los datos y se renderiza el componente, se genera la imagen
  useEffect(() => {
    if (data !== null) {
      const rafId = requestAnimationFrame(() => {
        handleGenerateImage();
      });
      return () => cancelAnimationFrame(rafId);
    }
  }, [data, handleGenerateImage, customBgImage]);

  const parseGrandPrixName = (fullName: string) => {
    if (!fullName) return <></>;

    const yearMatch = fullName.match(/\b\d{4}\b/);

    if (!yearMatch) {
      return (
        <>
          <label className="nig-title">{fullName.trim().toUpperCase()}</label>
          <label className="nig-gp">GRAND PRIX</label>
        </>
      );
    }

    const yearIndex = yearMatch.index!;
    const yearStr = yearMatch[0];

    const leftPart = fullName.substring(0, yearIndex + yearStr.length).trim();
    const remainder = fullName.substring(yearIndex + yearStr.length).trim();

    const gpMatch = remainder.match(/Grand Prix|GP/i);

    let title2 = remainder;
    if (gpMatch) {
      title2 = remainder.substring(0, gpMatch.index!).trim();
    }

    return (
      <>
        <label className="nig-year">{leftPart}</label>
        <label className="nig-title">{title2.toUpperCase()}</label>
        <label className="nig-gp">GRAND PRIX</label>
      </>
    );
  };

  useEffect(() => {
    if (data !== null) {
      const timer = setTimeout(() => {
        handleGenerateImage();
      }, 400);

      return () => clearTimeout(timer);
    }
  }, [data, handleGenerateImage]);

  const calculateTotalDistance = () => {
    if (!rallyLegs || rallyLegs.length === 0) return "0.0";

    const total = rallyLegs.reduce((acc, leg) => {
      // Extraer solo los números y el punto flotante del string de distancia
      const numericValue = parseFloat(
        leg.leg_distance?.replace(/[^0-9.]/g, "") || "0",
      );
      return acc + (isNaN(numericValue) ? 0 : numericValue);
    }, 0);

    return total.toFixed(1); // Mantiene un decimal (ej: 198.7)
  };
  const getAllStageWeathers = () => {
    if (!rallyLegs || rallyLegs.length === 0) return [];

    // Usamos flatMap para obtener un array plano de todos los weathers de todos los stages
    return rallyLegs.flatMap(
      (leg) =>
        leg.stages?.map((stage: any) => stage.weather?.toLowerCase()) || [],
    );
  };
  const getGlobalRallyConditions = () => {
    if (!rallyLegs || rallyLegs.length === 0) {
      return { main: "MIXED", sub: "-" };
    }

    // Obtenemos todos los climas de los stages
    const allWeathersArray = getAllStageWeathers();
    const combinedText = allWeathersArray.join(" ");

    // Obtenemos todos los stages de todos los legs
    const allStages = rallyLegs.flatMap((leg) => leg.stages || []);

    // Comprobamos si AL MENOS UN stage tiene lluvia real (tiene palabra de lluvia Y NO tiene "no rain" o "norain" en su propio texto)
    const hasRain = allStages.some((stage) => {
      const w = (stage.weather || "").toLowerCase();
      const isNegativeRain = w.includes("norain") || w.includes("no rain");
      const isPositiveRain =
        w.includes("rain") || w.includes("wet") || w.includes("shower");

      return !isNegativeRain && isPositiveRain;
    });

    // Hacemos lo mismo para la nieve de forma independiente por stage
    const hasSnow = allStages.some((stage) => {
      const w = (stage.weather || "").toLowerCase();
      const isNegativeSnow = w.includes("nosnow") || w.includes("no snow");
      const isPositiveSnow = w.includes("snow");

      return !isNegativeSnow && isPositiveSnow;
    });
    const hasClear =
      combinedText.includes("crisp clear") || combinedText.includes("clear");
    const hasCloud =
      combinedText.includes("cloud") || combinedText.includes("overcast");
    // Comprobar si todas las condiciones de TODOS los stages son exactamente iguales
    const firstWeather = allWeathersArray[0];
    const allIdentical = allWeathersArray.every(
      (w) => w === firstWeather && w.trim() !== "",
    );

    if (allIdentical && firstWeather) {
      return {
        main: firstWeather.toUpperCase(),
        sub: "CONSISTENT",
      };
    }

    // Si hay variedad entre los stages
    let subCondition = "VARIED STAGES";

    if (hasRain && hasSnow) {
      subCondition = "RAIN & SNOW";
    } else if (hasRain && hasClear) {
      subCondition = "CLEAR & RAIN";
    } else if (hasRain) {
      subCondition = "RAIN EXPECTED";
    } else if (hasSnow) {
      subCondition = "SNOW EXPECTED";
    } else if (hasCloud && hasClear) {
      subCondition = "PARTLY CLOUDY";
    }

    return {
      main: "MIXED",
      sub: subCondition,
    };
  };

  const getSurfaceIcon = (surfaceType: string) => {
    if (!surfaceType) return faRoad; // Fallback por defecto

    const type = surfaceType.toLowerCase();

    if (type.includes("snow")) {
      return faSnowflake;
    }
    if (
      type.includes("gravel") ||
      type.includes("dirt") ||
      type.includes("tierra")
    ) {
      return faMountain;
    }
    if (
      type.includes("tarmac") ||
      type.includes("asphalt") ||
      type.includes("asfalto")
    ) {
      return faRoad;
    }

    return faRoad; // Fallback si el texto no coincide
  };

  const renderConditionIcon = () => {
    if (!rallyLegs || rallyLegs.length === 0) {
      return <FontAwesomeIcon icon={faQuestionCircle} />;
    }

    const allWeathersArray = getAllStageWeathers();
    const combinedText = allWeathersArray.join(" ");
    // Obtenemos todos los stages de todos los legs
    const allStages = rallyLegs.flatMap((leg) => leg.stages || []);

    // Comprobamos si AL MENOS UN stage tiene lluvia real (tiene palabra de lluvia Y NO tiene "no rain" o "norain" en su propio texto)
    const hasRain = allStages.some((stage) => {
      const w = (stage.weather || "").toLowerCase();
      const isNegativeRain = w.includes("norain") || w.includes("no rain");
      const isPositiveRain =
        w.includes("rain") || w.includes("wet") || w.includes("shower");

      return !isNegativeRain && isPositiveRain;
    });

    // Hacemos lo mismo para la nieve de forma independiente por stage
    const hasSnow = allStages.some((stage) => {
      const w = (stage.weather || "").toLowerCase();
      const isNegativeSnow = w.includes("nosnow") || w.includes("no snow");
      const isPositiveSnow = w.includes("snow");

      return !isNegativeSnow && isPositiveSnow;
    });
    // Búsqueda normal para nubes (esto sigue igual basándose en tu array de stages)
    const hasCloud =
      combinedText.includes("cloud") || combinedText.includes("overcast");

    // Jerarquía de iconos
    if (hasRain) {
      return <FontAwesomeIcon icon={faCloudRain} />;
    }

    if (hasSnow) {
      return <FontAwesomeIcon icon={faSnowflake} />;
    }

    if (hasCloud) {
      return <FontAwesomeIcon icon={faCloud} />;
    }

    // Si no hay lluvia, nieve o nubes, asumimos cielo despejado (sun)
    return <FontAwesomeIcon icon={faSun} />;
  };

  useEffect(() => {
    if (
      data &&
      rallyLegs &&
      rallyLegs.length > 0 &&
      rallyLegs[0].stages != null
    ) {
      const totalDist = calculateTotalDistance();

      // Construimos un resumen detallado de las etapas por cada pierna (Leg)
      const legsSummary = rallyLegs
        .map(
          (leg) => `
- ${leg.leg} (${leg.leg_distance}):
  ${leg.stages
    .map(
      (s: any) =>
        `  * Tramo ${s.stage_number}: ${s.stage_name} - Distancia: ${s.distance} - Superficie: ${s.surface} - Clima: ${s.weather} - Neumáticos: ${s.tyre}`,
    )
    .join("\n")}
`,
        )
        .join("\n");

      // Contexto de clasificaciones agrupado por clase solo si la ronda es mayor a 1
      let standingsContext = "";
      if (data.round_number > 1) {
        let driversByClassText = "";
        if (data.driver_standings && data.driver_standings.length > 0) {
          const classesMap = new Map<string, any[]>();
          data.driver_standings.forEach((d: any) => {
            const className = d.class || "General";
            if (!classesMap.has(className)) {
              classesMap.set(className, []);
            }
            classesMap.get(className)!.push(d);
          });

          const driversLines: string[] = [];
          classesMap.forEach((drivers, className) => {
            const top = drivers
              .slice(0, 3)
              .map((d: any) => `${d.position}. ${d.username} (${d.points} pts)`)
              .join(", ");
            driversLines.push(`Clase ${className}: ${top}`);
          });
          driversByClassText = driversLines.join(" | ");
        }

        let teamsByClassText = "";
        if (data.team_standings && data.team_standings.length > 0) {
          const teamClassesMap = new Map<string, any[]>();
          data.team_standings.forEach((t: any) => {
            const className = t.class || "General";
            if (!teamClassesMap.has(className)) {
              teamClassesMap.set(className, []);
            }
            teamClassesMap.get(className)!.push(t);
          });

          const teamLines: string[] = [];
          teamClassesMap.forEach((teams, className) => {
            const top = teams
              .slice(0, 3)
              .map(
                (t: any) => `${t.position}. ${t.team_name} (${t.points} pts)`,
              )
              .join(", ");
            teamLines.push(`Clase ${className}: ${top}`);
          });
          teamsByClassText = teamLines.join(" | ");
        }

        if (driversByClassText || teamsByClassText) {
          standingsContext = `
Contexto de la general actual por clases (mención puramente opcional y secundaria si aporta narrativa, conservando textualmente cada nombre de piloto):
- Líderes de pilotos: ${driversByClassText || "No disponible"}
- Líderes de equipos: ${teamsByClassText || "No disponible"}
`;
        }
      }

      // Redactamos la estructura de texto que consumirá Gemini
      const promptText = `
Por favor, redacta una noticia previa atractiva y profesional para el siguiente evento de rally de la liga LigaVirtualF1:
- Campeonato: ${data.championship}
- Nombre del Rally: ${data.name} (Ronda ${data.round_number})
- Fecha de inicio: ${data.start_date}
- Fecha de finalización: ${data.date} a las ${data.time} (CET)
- Distancia total del rally: ${totalDist} km

Desglose de las etapas y condiciones:
${legsSummary}
${standingsContext}
Por favor, enfoca la noticia principalmente en los retos técnicos a los que se enfrentarán los pilotos, las condiciones meteorológicas esperadas en los diferentes tramos y la importancia de esta ronda. Si decides mencionar cómo llega la clasificación general, hazlo de forma muy sutil, tangencial y secundaria (sin darle protagonismo).

Necesito título de maximo 60 caracteres, 70 en casos muy extremos. Subtitulo de unos 100 caracteres, 150 maximo. Cuerpo de la noticia.
Lo necesito formateado con negritas en las partes importantes. Si me pones algun titulo/subtitulo en la noticia, utiliza el estilo de encabezado real de nivel 2. No me hagas enumeraciones con resultados o clasificaciones ya que debajo pondre un enlace a ellas.
    `.trim();
      setGeminiPromptText(promptText);
    }
  }, [data, readURL]);

  if (nextGrandsPrix == null) return <LoadingContent />;

  return (
    <div className="full-height padding-md app-container flex-center-top page-transition noverflow">
      <div className="form-container-md">
        <Title text="Pre GP Rally Generator" />
        <small className="mandatory-fields">(*) Required fields</small>

        <Form>
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>Grand Prix (*)</Form.Label>
              <Form.Select
                name="grand_prix"
                required
                onChange={(e) => {
                  setGrandPrixEventId(parseInt(e.target.value));
                }}
              >
                <option value="-1">Select a Grand Prix...</option>
                {nextGrandsPrix.map((gp) => {
                  return (
                    <option key={gp.id} value={gp.id}>
                      {gp.name}
                    </option>
                  );
                })}
              </Form.Select>
            </Form.Group>
          </Row>

          {/* Input para seleccionar imagen de fondo personalizada */}
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>
                Custom Background Image
                <span
                  className="profile-bubble"
                  data-tooltip="Upload an image to replace the default background for the Grand Prix."
                  data-flow="right"
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="label-icon"
                  />
                </span>
              </Form.Label>
              <Form.Control
                type="file"
                accept="image/*"
                onChange={handleCustomImageChange}
              />
            </Form.Group>
          </Row>

          {/* NUEVO: Input para la URL externa */}
          <Row className="mb-3">
            <Form.Group className="form-group" as={Col} md="12">
              <Form.Label>
                External Page URL
                <span
                  className="profile-bubble"
                  data-tooltip="Enter a URL to fetch its content in the console."
                  data-flow="right"
                >
                  <FontAwesomeIcon
                    icon={faQuestionCircle}
                    className="label-icon"
                  />
                </span>
              </Form.Label>
              <div className="d-flex gap-2">
                <Form.Control
                  type="url"
                  placeholder="https://rallysimfans.hu/rbr/rally_online.php?centerbox=rally_list_details.php&rally_id=99718"
                  value={targetUrl}
                  onChange={(e) => setTargetUrl(e.target.value)}
                />
                <Button
                  text={"Fetch URL"}
                  onClick={handleFetchUrlInfo}
                  className="button-neutral-lvf1"
                />
              </div>

              {/* Campos dinámicos generados en función de los Legs encontrados (Distancia, Carretera, Superficie y Clima) */}
              {rallyLegs.length > 0 && (
                <div className="flex-col gap-4 p-2 border rounded bg-gray-50 mt-3">
                  <h6 className="font-weight-bold text-secondary mb-2">
                    Configuration detected by Legs
                  </h6>
                  {rallyLegs.map((legItem, index) => (
                    <div
                      key={index}
                      className="row bg-white p-3 border rounded mb-2 mx-0 align-items-center"
                    >
                      {/* Distancia del Leg */}
                      <div className="col-md-3 mb-2 mb-md-0">
                        <label className="d-block text-xs font-weight-bold text-uppercase text-muted mb-1">
                          DISTANCE {legItem.leg}
                        </label>
                        <input
                          type="text"
                          value={legItem.leg_distance}
                          className="form-control form-control-sm"
                          onChange={(e) => {
                            const updatedLegs = [...rallyLegs];
                            updatedLegs[index].leg_distance = e.target.value;
                            setRallyLegs(updatedLegs);
                          }}
                        />
                      </div>

                      {/* Carretera / Road Type (Tire) del Leg */}
                      <div className="col-md-3 mb-2 mb-md-0">
                        <label className="d-block text-xs font-weight-bold text-uppercase text-muted mb-1">
                          Road ({legItem.leg})
                        </label>
                        <input
                          type="text"
                          value={legItem.roadType}
                          className="form-control form-control-sm"
                          onChange={(e) => {
                            const updatedLegs = [...rallyLegs];
                            updatedLegs[index].roadType = e.target.value;
                            setRallyLegs(updatedLegs);
                          }}
                        />
                      </div>

                      {/* Superficie del Leg */}
                      <div className="col-md-3 mb-2 mb-md-0">
                        <label className="d-block text-xs font-weight-bold text-uppercase text-muted mb-1">
                          Surface ({legItem.leg})
                        </label>
                        <input
                          type="text"
                          value={legItem.surface}
                          className="form-control form-control-sm"
                          onChange={(e) => {
                            const updatedLegs = [...rallyLegs];
                            updatedLegs[index].surface = e.target.value;
                            setRallyLegs(updatedLegs);
                          }}
                        />
                      </div>

                      {/* Clima del Leg */}
                      <div className="col-md-3">
                        <label className="d-block text-xs font-weight-bold text-uppercase text-muted mb-1">
                          Weather ({legItem.leg})
                        </label>
                        <input
                          type="text"
                          value={legItem.weather}
                          className="form-control form-control-sm"
                          onChange={(e) => {
                            const updatedLegs = [...rallyLegs];
                            updatedLegs[index].weather = e.target.value;
                            setRallyLegs(updatedLegs);
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </Form.Group>
          </Row>
        </Form>

        {isLoading && <LoadingContent />}

        {data != null && (
          <div
            style={{
              position: "absolute",
              zIndex: -1,
              opacity: 0,
              pointerEvents: "none",
            }}
          >
            <div ref={componentRef} className="prevral-container">
              <div
                className="prevral-background-overlay"
                style={
                  {
                    "--bg-gp-image": `url(${
                      customBgImage
                        ? customBgImage
                        : `/assets/ImagesGP/${data.last_grand_prix_event_id}.jpg`
                    })`,
                  } as React.CSSProperties
                }
              />
              <div className="nig-diagonal-overlay" />
              <p className="prevral-round">ROUND</p>
              <label className="prevral-number">
                {data.round_number.toString().padStart(2, "0")}
              </label>
              <label className="prevral-numberback">
                {data.round_number.toString().padStart(2, "0")}
              </label>
              {data?.flag_code && (
                <div className="nig-flags-container">
                  {data.flag_code.includes("+") ? (
                    // Si hay un '+', dividimos el string y pintamos las banderas correspondientes
                    data.flag_code.split("+").map((code, index) => {
                      const cleanCode = code.trim();
                      return (
                        <img
                          key={index}
                          src={`/assets/Flags/HighRes/${cleanCode}.png`}
                          alt={`${data.flag || "Country"} Flag ${index + 1}`}
                          onError={(e) => {
                            e.currentTarget.onerror = null;
                            e.currentTarget.src = `/assets/Flags/${cleanCode}.png`;
                          }}
                        />
                      );
                    })
                  ) : (
                    // Si es un código único sin '+'
                    <img
                      src={`/assets/Flags/HighRes/${data.flag_code}.png`}
                      alt={`${data.flag || "Country"} Flag`}
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = `/assets/Flags/${data.flag_code}.png`;
                      }}
                    />
                  )}
                </div>
              )}
              <div className="prevral-leftside">
                <p className="prevral-title">
                  <span>
                    {data.name.split("Rally of ")[0].toUpperCase()}{" "}
                    {`RALLY OF `}
                  </span>
                  <span>
                    {data.name.split("Rally of ").length > 1 &&
                      data.name.split("Rally of ")[1].toUpperCase()}
                  </span>
                </p>
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  viewBox="0 0 800 40"
                  width="100%"
                  height="100%"
                  className="prevral-line"
                >
                  <defs>
                    <linearGradient
                      id="fadeGradient"
                      x1="0%"
                      y1="0%"
                      x2="100%"
                      y2="0%"
                    >
                      <stop offset="0%" stop-color="#00a4a3" stop-opacity="1" />
                      <stop
                        offset="40%"
                        stop-color="#00a4a3"
                        stop-opacity="0.9"
                      />
                      <stop
                        offset="70%"
                        stop-color="#00a4a3"
                        stop-opacity="0.5"
                      />
                      <stop
                        offset="90%"
                        stop-color="#00a4a3"
                        stop-opacity="0.2"
                      />
                      <stop
                        offset="100%"
                        stop-color="#00a4a3"
                        stop-opacity="0"
                      />
                    </linearGradient>
                    <filter
                      id="glow"
                      x="-20%"
                      y="-50%"
                      width="140%"
                      height="200%"
                    >
                      <feGaussianBlur stdDeviation="3" result="blur1" />
                      <feGaussianBlur stdDeviation="6" result="blur2" />
                      <feMerge>
                        <feMergeNode in="blur2" />
                        <feMergeNode in="blur1" />
                        <feMergeNode in="SourceGraphic" />
                      </feMerge>
                    </filter>
                  </defs>
                  <path
                    d="M 60,16 
                      C 250,16 450,16 740,16
                      L 740,24
                      C 450,24 250,24 60,24
                      C 40,24 30,20 30,20
                      C 30,20 40,16 60,16 Z"
                    fill="url(#fadeGradient)"
                    filter="url(#glow)"
                  />
                </svg>

                <p className="prevral-date">
                  <FontAwesomeIcon icon={faCalendarAlt} />{" "}
                  <DateFormat
                    date={[data.start_date, data.date]}
                    format={"short"}
                    language={"en"}
                  />
                </p>
                <div className="prevral-stats-container">
                  <div className="prevral-stat-item">
                    <div className="prevral-stat-icon">
                      <FontAwesomeIcon icon={faRoad} />
                    </div>
                    <div className="prevral-stat-content">
                      <span className="prevral-stat-label">TOTAL STAGES</span>
                      <span className="prevral-stat-value prevral-cyan">9</span>
                      <span className="prevral-stat-sub">3 LEGS</span>
                    </div>
                  </div>

                  <div className="prevral-stat-divider"></div>
                  <div className="prevral-stat-item">
                    <div className="prevral-stat-icon ">
                      <FontAwesomeIcon icon={faLocationDot} />
                    </div>
                    <div className="prevral-stat-content">
                      <span className="prevral-stat-label">TOTAL DISTANCE</span>
                      <span className="prevral-stat-value prevral-cyan">
                        {calculateTotalDistance()}
                      </span>
                      <span className="prevral-stat-sub">KM</span>
                    </div>
                  </div>

                  <div className="prevral-stat-divider"></div>

                  <div className="prevral-stat-item">
                    <div className="prevral-stat-item">
                      <div className="prevral-stat-icon ">
                        {renderConditionIcon()}
                      </div>
                      <div className="prevral-stat-content">
                        <span className="prevral-stat-label">CONDITIONS</span>
                        <span className="prevral-stat-value prevral-cyan prevral-small">
                          {getGlobalRallyConditions().main}
                        </span>
                        <span className="prevral-stat-sub">
                          {getGlobalRallyConditions().sub}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="prevral-format-container">
                  <h3 className="prevral-format-title">RALLY FORMAT</h3>

                  <div className="prevral-format-box">
                    {rallyLegs.map((legItem, index) => {
                      const stagesCount = legItem.stages
                        ? legItem.stages.length
                        : 0;

                      return (
                        <div key={index} className="prevral-leg-column">
                          <div className="prevral-leg-header">
                            <span className="prevral-leg-badge">
                              {legItem.leg?.toUpperCase() || `LEG ${index + 1}`}
                            </span>
                            <span className="prevral-leg-stages">
                              {stagesCount} STAGES
                            </span>
                          </div>

                          <div className="prevral-leg-content">
                            {/* Distancia */}
                            <div className="prevral-stat-row">
                              <div className="prevral-stat-icon prevral-cyan">
                                <FontAwesomeIcon icon={faLocationDot} />
                              </div>
                              <div className="prevral-stat-text">
                                <span className="prevral-stat-label">
                                  DISTANCE
                                </span>
                                <span className="prevral-stat-value">
                                  {legItem.leg_distance || "0 KM"}
                                </span>
                              </div>
                            </div>

                            {/* Superficie / Carretera */}
                            {(() => {
                              const surfaceText =
                                legItem.roadType || legItem.surface || "";

                              return (
                                <div className="prevral-stat-row">
                                  <div className="prevral-stat-icon prevral-cyan">
                                    {/* Llamamos a la función para seleccionar el icono dinámicamente */}
                                    <FontAwesomeIcon
                                      icon={getSurfaceIcon(surfaceText)}
                                    />
                                  </div>
                                  <div className="prevral-stat-text">
                                    <span className="prevral-stat-label">
                                      SURFACE
                                    </span>
                                    <span className="prevral-stat-value">
                                      {surfaceText || "-"}
                                    </span>
                                  </div>
                                </div>
                              );
                            })()}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
              <div className="prevral-bar-container">
                {/* Columna 1: Championship */}
                <div className="prevral-bar-item">
                  <div className="prevral-bar-icon">
                    <FontAwesomeIcon icon={faTrophy} />
                  </div>
                  <div className="prevral-bar-text">
                    <span className="prevral-bar-label">CHAMPIONSHIP</span>
                    <span className="prevral-bar-value">
                      {data.championship}
                    </span>
                  </div>
                </div>

                <div className="prevral-bar-divider"></div>

                {/* Columna 2: Next Event */}
                <div className="prevral-bar-item">
                  <div className="prevral-bar-icon">
                    <FontAwesomeIcon icon={faCalendarAlt} />
                  </div>
                  <div className="prevral-bar-text">
                    <span className="prevral-bar-label">NEXT EVENT</span>
                    <span className="prevral-bar-value ">
                      ROUND{" "}
                      {(data.round_number + 1).toString().padStart(2, "0")}
                    </span>
                  </div>
                </div>

                <div className="prevral-bar-divider"></div>

                {/* Columna 3: More Info */}
                <div className="prevral-bar-item">
                  <div className="prevral-bar-icon">
                    <FontAwesomeIcon icon={faGlobe} />
                  </div>
                  <div className="prevral-bar-text">
                    <span className="prevral-bar-label">MORE INFO</span>
                    <span className="prevral-bar-value">LIGAVIRTUALF1.ES</span>
                  </div>
                </div>
              </div>
              <img
                className="prevral-logo"
                src={require(`../../../assets/Images/Logo.png`)}
              />
              <div className="prevral-footer"></div>
              <div className="prevral-box"></div>
              <div className="prevral-square"></div>
            </div>
          </div>
        )}

        {generatedImageUrl && (
          <div className="nig-preview-container mt-4">
            <img
              src={generatedImageUrl}
              alt="Preview Noticia GP"
              className="nig-preview-img"
              style={{ width: "100%", height: "auto", borderRadius: "8px" }}
            />
            <div className="mt-3">
              <Button
                text="Download Final Image"
                icon={faDownload}
                iconPosition="left"
                onClick={() => {
                  const link = document.createElement("a");
                  link.href = generatedImageUrl;
                  link.download = `GP_${data?.name}_Noticia.png`;
                  link.click();
                }}
              />
            </div>
          </div>
        )}
        {generatedImageUrl && <GeminiRequest text={geminiPromptText} />}
      </div>
    </div>
  );
};

export default PreGPRallyGenerator;
